<template>
  <div class="company-values-wrapper">
    <div class="pill-row">
      <button v-for="(val, idx) in currentValues" :key="`pill-${idx}-${val}`" type="button" class="pill" disabled>
        {{ val }}
      </button>
      <button type="button" class="pill edit-pill" @click="openModal">
        <font-awesome-icon :icon="['fal', 'pen-to-square']" />
        Edit
      </button>
    </div>

    <b-modal
      ref="modalRef"
      title="Select award values"
      centered
      @show="onModalShow"
      @hidden="onModalHidden"
      body-class="p-0"
      dialog-class="modal-dialog"
    >
      <div class="px-5 py-4 pb-5 modal-body-content">
        <div class="intro">
          <div class="sub">Select your company values to tag against this award. You can also add new ones.</div>
        </div>

        <div class="checkbox-list">
          <app-input v-model="selectedArray" type="checkbox" :options="checkboxOptions" stacked />
        </div>

        <div v-if="newEntries.length > 0" class="add-new-list">
          <div v-for="(entry, idx) in newEntries" :key="`new-${idx}`" class="new-row">
            <app-input
              v-model="newEntries[idx]"
              :validate-immediate="false"
              :name="`New value ${idx + 1}`"
              type="text"
              placeholder="e.g. Being Brave"
              class="new-input"
              @input="onNewInput(idx, $event)"
              @blur="onNewBlur(idx)"
            />
            <b-button variant="outline-danger" size="xs" class="remove-btn" @click="removeEntry(idx)">
              <font-awesome-icon :icon="['fad', 'trash']" />
            </b-button>
          </div>
        </div>

        <div class="footer-actions">
          <b-button variant="outline-primary" @click="addEntry" size="sm">
            <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />
            Add new
          </b-button>
        </div>

        <small v-if="validationError" class="text-danger">{{ validationError }}</small>
      </div>

      <template #modal-footer>
        <b-button variant="outline-primary" @click="closeModal">Cancel</b-button>
        <b-button variant="primary" @click="saveAndClose">Save & close</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import AppInput from '@/components/AppInput.vue';
import ApiClient from '@/ApiClient';

const props = defineProps({
  value: {
    // Accept array of strings or array of { name }
    type: [Array, null],
    default: () => [],
  },
});
const emit = defineEmits(['input']);

const modalRef = ref(null);
const validationError = ref('');
const suggestions = ref([]); // string[]
const selectedArray = ref([]); // string[]
const newEntries = ref([]); // string[]

const normalizeToNames = list => {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .map(v => (v && typeof v === 'object' ? v.name : v))
    .map(s => String(s || '').trim())
    .filter(Boolean);
};

const currentValues = computed(() => {
  const names = normalizeToNames(props.value);
  return names;
});

const displayedOptions = computed(() => {
  const all = new Set([...(suggestions.value || []), ...currentValues.value]);
  return Array.from(all).sort((a, b) => a.localeCompare(b));
});

const checkboxOptions = computed(() => displayedOptions.value.map(v => ({ text: v, value: v })));

const openModal = () => {
  if (modalRef.value) {
    modalRef.value.show();
  }
};

const closeModal = () => {
  if (modalRef.value) {
    modalRef.value.hide();
  }
};

const fetchSuggestionsFromPrograms = async () => {
  try {
    const programs = await ApiClient.getRecognitionPrograms();
    const set = new Set();
    (programs || []).forEach(p => {
      const vals = p?.properties?.company_values || [];
      vals.forEach(cv => {
        const name = typeof cv === 'object' ? cv?.name : cv;
        const trimmed = String(name || '').trim();
        if (trimmed) {
          set.add(trimmed);
        }
      });
    });
    suggestions.value = Array.from(set);
  } catch {
    suggestions.value = [];
  }
};

const onModalShow = async () => {
  validationError.value = '';
  await fetchSuggestionsFromPrograms();
  selectedArray.value = [...currentValues.value];
  newEntries.value = [];
};

const onModalHidden = () => {
  validationError.value = '';
  newEntries.value = [];
};

const addEntry = () => {
  newEntries.value.push('');
};

const removeEntry = idx => {
  newEntries.value.splice(idx, 1);
};

const sanitizeNew = raw => {
  return String(raw ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
};
const onNewInput = (idx, value) => {
  newEntries.value[idx] = sanitizeNew(value);
};
const onNewBlur = idx => {
  newEntries.value[idx] = sanitizeNew(newEntries.value[idx]);
};

const saveAndClose = () => {
  validationError.value = '';

  const additions = newEntries.value.map(v => sanitizeNew(v)).filter(v => v.length > 0);

  const allSelected = new Set([...(selectedArray.value || []), ...additions]);
  const result = Array.from(allSelected);

  // Emit as array of strings (names)
  emit('input', result);
  closeModal();
};
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.company-values-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.pill-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pill {
  border: 1px solid var(--palette-color-default-lighten-80);
  background: var(--palette-color-base-white);
  color: var(--palette-color-default);
  border-radius: 50rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
}

.pill.active {
  /* no special styling for active in this context */
}

.pill.edit-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease, color 120ms ease, background-color 120ms ease;
}

.pill.edit-pill:hover {
  color: var(--palette-color-brand-primary);
  background: var(--palette-color-core-1-lighten-80);
  border-color: var(--palette-color-brand-primary-lighten-30);
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.intro .sub {
  color: var(--palette-color-default-lighten-30);
}

.editor-list {
  display: none;
}

.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.checkbox-list :deep(.custom-control.custom-checkbox .custom-control-label::before),
.checkbox-list :deep(.custom-control.custom-checkbox .custom-control-label::after) {
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border-width: 1px;
}
.checkbox-list :deep(.custom-control.custom-checkbox .custom-control-label) {
  font-size: 0.875rem;
  padding-left: 1rem;
  line-height: 2rem;
  user-select: none;
}

/* Bootstrap 5 form-check variant */
.checkbox-list :deep(.form-check-input) {
  width: 2rem;
  height: 2rem;
}
.checkbox-list :deep(.form-check-label) {
  font-size: 0.875rem;
  line-height: 2rem;
  user-select: none;
  padding-left: 1rem;
}

/* Spacing between option rows */
.checkbox-list :deep(.custom-control),
.checkbox-list :deep(.form-check) {
  margin-bottom: 0.75rem;
}
.checkbox-list :deep(.custom-control:last-child),
.checkbox-list :deep(.form-check:last-child) {
  margin-bottom: 0;
}

.add-new-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.new-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.new-input {
  margin: 0;
  width: 100%;
}
.remove-btn {
  width: fit-content;
  padding: 0.25rem 0.5rem;
}
.footer-actions {
  margin-top: 0.25rem;
}

:deep(.modal-dialog) {
  max-width: 550px;
}
</style>
